import * as R from 'ramda';
import { pure, compose, lifecycle, withHandlers } from 'react-recompose';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import withLinehaulAdjustments from '../../../hocs/with-linehaul-adjustments';
import { getInvoiceGlCode2, withComponentDidUpdatePropCallback } from '../../../hocs';
// component charge
import { recalculateAllCharges } from '../formik/helpers';
import {
  setGlCodeToCharges,
  withRecalculateChargesOnChangeTotals,
  withRecalculateChargesOnChangeRecalculateId,
} from './';
//////////////////////////////////////////////////

const withRecalculateAllCharges = compose(
  withHandlers({
    handleRecalculateAllCharges: () => (propName, props) => {
      const { rateProps, isInvoice, isCarrier, isEditMode } = props;

      const { values, setValues } = rateProps;

      const {
        charges,
        fleetVendorCharges,
        carrierRateCharges,
        primaryDriverCharges,
        secondaryDriverCharges,
      } = values;

      if (G.isAllTrue(isInvoice, isCarrier, isEditMode)) {
        const newValues = R.assoc(
          GC.FIELD_CHARGES,
          recalculateAllCharges(charges, props),
          values,
        );

        return setValues(newValues);
      }

      if (G.isTrue(isCarrier)) {
        const newValues = R.assoc(
          GC.FIELD_CARRIER_RATE_CHARGES,
          recalculateAllCharges(carrierRateCharges, props),
          values,
        );

        return setValues(newValues);
      }

      const newValues = {
        ...values,
        [GC.FIELD_CHARGES]: recalculateAllCharges(charges, props),
        [GC.FIELD_FLEET_VENDOR_CHARGES]: recalculateAllCharges(fleetVendorCharges, props),
        [GC.FIELD_PRIMARY_DRIVER_CHARGES]: recalculateAllCharges(primaryDriverCharges, props),
        [GC.FIELD_SECONDARY_DRIVER_CHARGES]: recalculateAllCharges(secondaryDriverCharges, props),
      };

      setValues(newValues);
    },
  }),
  withRecalculateChargesOnChangeTotals,
  withRecalculateChargesOnChangeRecalculateId,
  pure,
);

const withInitialGLCodeMappings = compose(
  withHandlers({
    handleSetInitialGLCodes: (props) => () => {
      const { rateProps, isInvoice, isCarrier, isEditMode, isFleetInvoice, glCodeMappings, chargesArrayName } = props;

      const { values, setValues } = rateProps;
      const { carrierRateCharges } = values;

      if (R.not(isInvoice)) return;

      if (R.and(isInvoice, G.isTrue(isEditMode))) return;

      const invoiceCurrency = G.getCurrencyFromObject2(values);

      if (G.isTrue(isCarrier)) {
        const newValues = R.assoc(
          GC.FIELD_CARRIER_RATE_CHARGES,
          setGlCodeToCharges(recalculateAllCharges(carrierRateCharges, props), glCodeMappings, invoiceCurrency),
          values,
        );

        return setValues(newValues);
      }

      if (G.isTrue(isFleetInvoice)) {
        const { primaryDriverCharges, fleetVendorCharges, secondaryDriverCharges } = values;

        const newValues = {
          ...values,
          fleetVendorCharges: setGlCodeToCharges(fleetVendorCharges, glCodeMappings, invoiceCurrency),
          primaryDriverCharges: setGlCodeToCharges(primaryDriverCharges, glCodeMappings, invoiceCurrency),
          secondaryDriverCharges: setGlCodeToCharges(secondaryDriverCharges, glCodeMappings, invoiceCurrency),
        };

        return setValues(newValues);
      }

      const currentCharges = R.pathOr([], ['values', chargesArrayName], rateProps);

      const newValues = {
        ...values,
        [chargesArrayName]: setGlCodeToCharges(currentCharges, glCodeMappings, invoiceCurrency),
      };

      setValues(newValues);
    },
    handleChangeCurrency: (props) => (propName, newProps, prevProps) => {
      const {
        rateProps,
        isInvoice,
        isCarrier,
        isDriverRate,
        isCarrierRate,
        glCodeMappings,
        isFleetInvoice,
        chargesArrayName,
      } = props;

      const { values, setValues } = rateProps;

      const convertedTotal = G.convertCurrencyByExchangeRateToFloatOrNull(
        prevProps.customerTotal,
        prevProps.currency,
        newProps.currency,
      );

      if (R.not(isInvoice)) {
        if (R.or(isDriverRate, isCarrierRate)) {
          return setValues(R.assoc(GC.FIELD_CUSTOMER_TOTAL, convertedTotal, values));
        }

        return;
      }

      const currentCharges = R.pathOr([], ['values', chargesArrayName], rateProps);
      const invoiceCurrency = G.getPropFromObject(propName, newProps);
      const invoiceGlCode = getInvoiceGlCode2(glCodeMappings, invoiceCurrency);

      if (G.isTrue(isFleetInvoice)) {
        const { primaryDriverCharges, fleetVendorCharges, secondaryDriverCharges } = values;

        const newValues = {
          ...values,
          [GC.FIELD_CUSTOMER_TOTAL]: convertedTotal,
          [GC.FIELD_GL_CODE]: G.fromNullToEmptyString(invoiceGlCode),
          fleetVendorCharges: setGlCodeToCharges(fleetVendorCharges, glCodeMappings, invoiceCurrency),
          primaryDriverCharges: setGlCodeToCharges(primaryDriverCharges, glCodeMappings, invoiceCurrency),
          secondaryDriverCharges: setGlCodeToCharges(secondaryDriverCharges, glCodeMappings, invoiceCurrency),
        };

        return setValues(newValues);
      }

      if (G.isTrue(isCarrier)) {
        const newValues = {
          ...values,
          [GC.FIELD_CUSTOMER_TOTAL]: convertedTotal,
          [GC.FIELD_GL_CODE]: G.fromNullToEmptyString(invoiceGlCode),
          [chargesArrayName]: setGlCodeToCharges(currentCharges, glCodeMappings, invoiceCurrency),
        };

        return setValues(newValues);
      }

      const newValues = {
        ...values,
        [GC.FIELD_GL_CODE]: G.fromNullToEmptyString(invoiceGlCode),
        [chargesArrayName]: setGlCodeToCharges(currentCharges, glCodeMappings, invoiceCurrency),
      };

      setValues(newValues);
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'glCodeMappings',
    callbackName: 'handleSetInitialGLCodes',
  }),
  withComponentDidUpdatePropCallback({
    propName: GC.FIELD_CURRENCY,
    callbackName: 'handleChangeCurrency',
  }),
  pure,
);

const isItemLengthMatrixCharge = R.propEq(
  GC.ACCESSORIAL_MATRIX_TYPE_ITEM_LENGTH,
  GC.FIELD_ACCESSORIAL_MATRIX_TYPE,
);

const replaceItemLengthMatrixCharges = (charges, sharedAccessorials) => R.concat(
  R.reject(isItemLengthMatrixCharge, R.or(charges, [])),
  R.filter(isItemLengthMatrixCharge, R.or(sharedAccessorials, [])),
);

const matrixChargeFields = [
  GC.FIELD_ID,
  GC.FIELD_GUID,
  GC.FIELD_CHARGE_RATE,
  GC.FIELD_ACCESSORIAL_MATRIX_UOM,
  GC.FIELD_ACCESSORIAL_MATRIX_TYPE,
  GC.FIELD_ACCESSORIAL_MATRIX_ITEM_INFO,
  GC.FIELD_ACCESSORIAL_CONFIG_GUID,
  GC.FIELD_TRANSPORTATION_MODE_GUID,
];

const getMatrixChargesForComparison = (charges) => R.compose(
  R.map(R.pick(matrixChargeFields)),
  R.filter(isItemLengthMatrixCharge),
)(R.or(charges, []));

const shouldSuppressInitialMatrixCharges = (props) => {
  const { rateProps, isInvoice, isEditMode } = props;

  const allowItemLengthMatrixOnEdit = G.isTrue(R.prop('allowItemLengthMatrixOnEdit', rateProps));

  return G.isAllTrue(
    G.isTrue(isEditMode),
    R.not(isInvoice),
    R.not(allowItemLengthMatrixOnEdit),
  );
};

const getSelectedRateEntityGuid = (props) => {
  const { rateProps, isDriverRate, isCarrierRate, chargesArrayName } = props;

  const values = R.propOr({}, 'values', rateProps);

  if (G.isTrue(isDriverRate)) {
    const driverGuidField = R.equals(chargesArrayName, GC.FIELD_SECONDARY_DRIVER_CHARGES)
      ? GC.FIELD_SECONDARY_DRIVER_GUID
      : GC.FIELD_PRIMARY_DRIVER_GUID;

    return R.prop(driverGuidField, values);
  }

  if (G.isTrue(isCarrierRate)) {
    return R.path([
      GC.FIELD_PREFIX_CARRIER_ASSIGNMENT,
      GC.FIELD_CARRIER_GUID,
    ], values);
  }

  return true;
};

const hasSelectedRateEntity = R.compose(
  G.isNotNilAndNotEmpty,
  getSelectedRateEntityGuid,
);

const withItemLengthMatrixAccessorials = compose(
  withHandlers({
    handleSetItemLengthMatrixAccessorials: (props) => () => {
      const { rateProps, chargesArrayName } = props;
      const { values, setValues, sharedAccessorialsApplyTo } = rateProps;

      if (R.isNil(sharedAccessorialsApplyTo)) return;

      if (R.not(hasSelectedRateEntity(props))) return;

      const charges = R.pathOr([], [chargesArrayName], values);

      const matrixAccessorials = G.filterChargesByModeOrNoMode(
        sharedAccessorialsApplyTo,
        R.prop(GC.FIELD_MODE, values),
      );

      if (R.equals(
        getMatrixChargesForComparison(charges),
        getMatrixChargesForComparison(matrixAccessorials),
      )) return;

      const chargesToUse = replaceItemLengthMatrixCharges(charges, matrixAccessorials);
      const newValues = R.assoc(chargesArrayName, recalculateAllCharges(chargesToUse, props), values);

      setValues(newValues);
    },
  }),
  lifecycle({
    componentDidMount() {
      if (shouldSuppressInitialMatrixCharges(this.props)) return;

      this.props.handleSetItemLengthMatrixAccessorials();
    },
    componentDidUpdate(prevProps) {
      const previousAccessorials = R.path(['rateProps', 'sharedAccessorialsApplyTo'], prevProps);
      const currentAccessorials = R.path(['rateProps', 'sharedAccessorialsApplyTo'], this.props);
      const previousRecalculateId = R.prop('recalculateAllChargesId', prevProps);
      const currentRecalculateId = R.prop('recalculateAllChargesId', this.props);

      const accessorialsChanged = R.not(R.equals(previousAccessorials, currentAccessorials));
      const recalculateIdChanged = R.not(R.equals(previousRecalculateId, currentRecalculateId));

      const previousRateEntityGuid = getSelectedRateEntityGuid(prevProps);
      const currentRateEntityGuid = getSelectedRateEntityGuid(this.props);

      const selectedRateEntityChanged = G.isAllTrue(
        G.isNotNilAndNotEmpty(currentRateEntityGuid),
        R.not(R.equals(previousRateEntityGuid, currentRateEntityGuid)),
      );

      const shouldSync = shouldSuppressInitialMatrixCharges(this.props)
        ? recalculateIdChanged
        : G.isAnyTrue(accessorialsChanged, recalculateIdChanged, selectedRateEntityChanged);

      if (shouldSync) {
        this.props.handleSetItemLengthMatrixAccessorials();
      }
    },
  }),
);

export const withDMCharges = compose(
  withItemLengthMatrixAccessorials,
  withRecalculateAllCharges,
  withLinehaulAdjustments,
  withInitialGLCodeMappings,
);
